import Page from '@modules/page'
import {navigate, useParams} from "@reach/router"
import {ContextualSaveBar, Layout, PageActions} from "@shopify/polaris"
import {ContextProvider, useStore} from "./container"
import TitleCard from "./components/title"
import Display from "./components/display"
import LayoutSection from "@modules/layoutSection"
import Preview from "./components/preview"
import {useCallback, useEffect, useState} from "react"
import API from "@helpers/api"
import {showToast} from "@plugins/toast"
import {Skeleton} from "@modules/skeleton"
import {CancelToken} from "@plugins/axios"
import {checkRequiredFields} from "@helpers/utils"
import StatusBadge from "@components/statusBadge"
import ChartContent from "./components/content"
import {showConfirm} from "@plugins/confirm"
import { isEqual, omit } from 'lodash'
import DiscardModal from '../../components/discardModal'

const EditSizeChart = () => {
	return (
		<ContextProvider>
			<EditSizeChartView />
		</ContextProvider>
	)
}

const EditSizeChartView = () => {
	const [ready, setReady] = useState(false)
	const [saving, setSaving] = useState(false)
	let [error, setError] = useState([])
	const {pageID} = useParams()
	const {state, dispatch} = useStore()
	const source = CancelToken.source()
	const {title, active, _id} = state

	const [activeModal, setActiveModal] = useState(false);
	const [prevState, setPrevState] = useState({})

	const isEqualState = isEqual(omit(state, ['created_at', 'updated_at', 'id', 'updatedAt']), omit(prevState, ['updatedAt']))

	const handleModalChange = useCallback(() => setActiveModal(!activeModal), [activeModal]);

	useEffect(() => {
		const handleReloadPage = (e) => {
		  e.preventDefault()
		  e.returnValue = ""
		}
		if(isEqualState === false) window.addEventListener("beforeunload", handleReloadPage)
		return () => window.removeEventListener("beforeunload", handleReloadPage)
	  }, [isEqualState])

	useEffect(() => {
		let result
		const fetchData = async () => {
			try {
				result = await API.getDetail('size-chart', pageID, source.token)
				console.log('Data details', result)
				if (result && result.ok && result.payload) {
					dispatch({type: 'setData', payload: result.payload})
					if (!result.payload.hasOwnProperty("target_collection")) {
						result.payload.target_collection = ""
					}
					setPrevState(result.payload)
					setReady(true)
				}
			} catch (error) {
				console.log(error)
			}
		}
		fetchData()
		return () => source.cancel()
	}, [pageID])
	const handleSave = async () => {
		error = await checkRequiredFields(['chart_content', 'title'], state)
		if (error.length > 0) {
			setError(error)
			window.scrollTo({top: 0, behavior: 'smooth'})
			return showToast({
				message: 'Please fill in all the mandatory fields',
				error: true
			})
		}

		setSaving(true)
		const {target_products, target_collection, schedule_time, schedule} = state
		API.update('size-chart', {
			...state,
			target_products: target_products?.filter(Boolean).map(p => p.value),
			target_collection: target_collection?.value,
			schedule_time: schedule ? schedule_time : new Date()
		}).then((res) => {
			if (res.ok) {
				setPrevState({
					...prevState,
					...res.payload,
					target_products: target_products,
					target_collection: target_collection
				})
				return showToast({
					message: 'Updated successfully'
				})
			}
		}).finally(() => setSaving(false))
	}

	const handleDelete = () => {
		showConfirm({
			title: `Delete ${title}?`,
			message: 'This can’t be undone.',
			danger: true,
			confirm: 'Delete'
		}).then(res => {
			if (res) {
				API.bulkDelete('size-chart', [_id]).then((res) => {
					if (res.ok) {
						showToast({
							message: 'Size chart deleted'
						})
						return navigate('/size-chart')
					} else {
						showToast({
							message: 'Cannot delete, please try again',
							error: true
						})
					}
				})
			}
		})
	}

	return ready ? (
		<Page
			title={title}
			breadcrumbs={[{content: 'Size chart', onAction: () => isEqualState ? navigate('/size-chart') : handleModalChange()}]}
			titleMetadata={<StatusBadge status={active} />}
			fullWidth
		>
			{!isEqualState && <ContextualSaveBar
				message="Unsaved changes"
				saveAction={{
					onAction: handleSave,
					loading: saving,
					disabled: false,
				}}
				discardAction={{
					onAction: () => handleModalChange()
				}}
				fullWidth
			/>}
			<Layout>
				<Layout.Section secondary>
					<TitleCard error={error} />
					<ChartContent error={error} />
					<Display />
				</Layout.Section>
				<LayoutSection isStuck>
					<Preview />
				</LayoutSection>
			</Layout>
			<PageActions
				primaryAction={{
					content: 'Save',
					onAction: handleSave,
					loading: saving

				}}
				secondaryActions={[
					{
						content: 'Delete',
						destructive: true,
						onAction: handleDelete

					},
				]}
			/>
			<DiscardModal	 
				activeModal={activeModal}
				handleModalChange={handleModalChange}
				backLink='/size-chart'
			/>
		</Page>
	) : <Skeleton />
}

export default EditSizeChart
